import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { ArrowLeft, Menu } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { CategoryManager } from "../../components/admin/CategoryManager";
import { SubcategoryManager } from "../../components/admin/SubcategoryManager";
import {
  SidebarToggle,
  useAdminLayout,
} from "../../components/admin/AdminLayout";

type Category = {
  id: string;
  name: string;
  slug: string;
  created_at?: string;
  updated_at?: string;
};

const AdminCategories = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useAdminLayout();

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();

      if (!data.session) {
        navigate("/admin/login");
        return;
      }

      // Kiểm tra quyền admin
      const { data: userData, error } = await supabase
        .from("admin_users")
        .select("*")
        .eq("user_id", data.session.user.id)
        .single();

      if (error || !userData) {
        await supabase.auth.signOut();
        navigate("/admin/login");
        return;
      }

      loadCategories();
    };

    checkAuth();
  }, [navigate]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setCategories(data || []);
    } catch (error: Error | unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi không xác định";
      toast({
        title: "Lỗi",
        description: "Không thể tải danh sách danh mục",
        variant: "destructive",
      });
      console.error("Error loading categories:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center mb-6">
        {isMobile && <SidebarToggle className="mx-2" />}
        <h1 className="text-2xl font-bold">Quản lý danh mục</h1>
      </div>

      <div className="w-full">
        {/* Header với menu responsive */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center justify-between">
          </div>
        </div>

        {/* Desktop Tabs */}
        <Tabs
          defaultValue="categories"
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full md:block"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="categories">Danh mục</TabsTrigger>
            <TabsTrigger value="subcategories">Danh mục con</TabsTrigger>
          </TabsList>
          <TabsContent value="categories" className="mt-4">
            <Card className="w-full">
              <CardHeader className="p-4 md:p-6">
                <CardTitle>Quản lý danh mục</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <CategoryManager
                  categories={categories}
                  onCategoriesChange={loadCategories}
                  isLoading={loading}
                />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="subcategories" className="mt-4">
            <Card className="w-full">
              <CardHeader className="p-4 md:p-6">
                <CardTitle>Quản lý danh mục con</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <SubcategoryManager
                  categories={categories}
                  onSubcategoriesChange={loadCategories}
                  isLoading={loading}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Mobile Content Display */}
        <div className="md:hidden">
          {activeTab === "categories" ? (
            <Card className="w-full mt-4">
              <CardHeader className="p-4">
                <CardTitle>Quản lý danh mục</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <CategoryManager
                  categories={categories}
                  onCategoriesChange={loadCategories}
                  isLoading={loading}
                />
              </CardContent>
            </Card>
          ) : (
            <Card className="w-full mt-4">
              <CardHeader className="p-4">
                <CardTitle>Quản lý danh mục con</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <SubcategoryManager
                  categories={categories}
                  onSubcategoriesChange={loadCategories}
                  isLoading={loading}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
